import { api, type Box, myBoxId } from '../console/api.ts';
import { empty, field, pageHead, pill } from '../console/components.ts';
import { boxName, date, esc, fmt, icon, type Money, person } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { has, nameOf, runsCashBox, supervises } from '../console/session.ts';
import { act, qInput, textInput } from '../console/ui.ts';
import { moneyControl, textControl } from '../controls.ts';

/**
 * «Caja»: the cashier opens her box with the day's float, deposits to the bank and closes it
 * counting the cash; the direction sees every box and approves a difference.
 */

/** A session of a box as its history keeps it (GET /v1/cash-boxes/:id/sessions). */
export type BoxSession = {
  id: string;
  cashBoxId: string;
  state: string;
  openedBy: string;
  openedOn: string;
  float: Money;
  byMethod: Record<'CASH' | 'TRANSFER' | 'CARD' | 'PAYMENT_LINK', Money>;
  refunded: Money;
  deposited: Money;
  expected: Money;
  counted: Money | null;
  difference: Money | null;
  closedBy: string | null;
  closedOn: string | null;
  approvedBy: string | null;
  approvedOn: string | null;
};
type SessionPayment = {
  id: string;
  installment: number;
  amount: Money;
  method: string;
  reference: string;
  capturedOn: string;
  capturedBy: string;
  policyNumber: string | null;
  policyholder: string | null;
};
const METHODS: [keyof BoxSession['byMethod'], string][] = [
  ['CASH', 'Efectivo'],
  ['TRANSFER', 'Transferencia'],
  ['CARD', 'Tarjeta'],
  ['PAYMENT_LINK', 'Enlace de pago'],
];
const METHOD_LABEL = Object.fromEntries(METHODS) as Record<string, string>;

/** A difference in its color and with its word (never color alone): faltante or sobrante. */
const differenceHtml = (d: Money | null) =>
  !d
    ? '<span class="muted">—</span>'
    : d.amountMinor === 0
      ? `${fmt(d)}<div class="muted small">cuadra</div>`
      : `<strong style="color:var(${d.amountMinor < 0 ? '--bad' : '--warn'})">${fmt(d)}</strong><div class="muted small">${d.amountMinor < 0 ? 'faltante' : 'sobrante'}</div>`;

/**
 * The arqueo by method of the open session: what the cashier collected by each method (cash
 * into the box, the rest at the bank), what she gave back and deposited, and the cash expected.
 */
export function arqueoHtml(s: BoxSession) {
  return `<div class="table-wrap" data-component="table" data-arqueo><table class="table breakdown"><caption class="sr-only">Arqueo por método de la sesión abierta</caption><tbody>
    ${METHODS.map(([k, label]) => `<tr><th scope="row">${esc(label)}</th><td class="num">${fmt(s.byMethod[k])}</td></tr>`).join('')}
    <tr><th scope="row">Fondo inicial</th><td class="num">${fmt(s.float)}</td></tr>
    <tr><th scope="row">Efectivo devuelto</th><td class="num">${fmt(s.refunded)}</td></tr>
    <tr><th scope="row">Depositado al banco</th><td class="num">${fmt(s.deposited)}</td></tr>
    <tr class="total"><th scope="row">Efectivo que debe haber</th><td class="num">${fmt(s.expected)}</td></tr></tbody></table></div>`;
}

/**
 * The history of a box: each session, newest first, with its float, what it collected by
 * method, the cash given back, deposited and counted, the difference and who approved it; each
 * session opens its payments.
 */
export function historyHtml(id: string, sessions: BoxSession[], title: string) {
  if (!sessions.length)
    return `<section class="card" data-box-history="${esc(id)}"><div class="card-head"><h2>${esc(title)}</h2></div><p class="muted policy-note">Esta caja todavía no tiene sesiones.</p></section>`;
  const rows = sessions
    .map(
      (
        x,
      ) => `<tr><td class="nowrap">${esc(date(x.openedOn))}<div class="muted small">${esc(person(x.openedBy))} · fondo ${fmt(x.float)}</div></td>
        ${METHODS.map(([k]) => `<td class="num">${fmt(x.byMethod[k])}${k === 'CASH' && x.refunded.amountMinor ? `<div class="muted small">devuelto ${fmt(x.refunded)}</div>` : ''}</td>`).join('')}
        <td class="num">${fmt(x.deposited)}</td>
        <td class="num">${fmt(x.counted)}</td><td class="num">${differenceHtml(x.difference)}</td>
        <td>${pill(x.state)}${x.approvedBy ? `<div class="muted small">aprobó ${esc(person(x.approvedBy))}</div>` : ''}${x.closedOn ? `<div class="muted small">cerró el ${esc(date(x.closedOn))}</div>` : ''}</td>
        <td class="actions"><button type="button" class="btn sm" data-session-payments="${esc(x.id)}" data-box="${esc(id)}" aria-expanded="false">Ver pagos</button></td></tr>
        <tr data-session-detail="${esc(x.id)}" hidden><td colspan="10"></td></tr>`,
    )
    .join('');
  return `<section class="card" data-box-history="${esc(id)}"><div class="card-head"><h2>${esc(title)}</h2><span class="spacer"></span><span class="muted small">${sessions.length} ${sessions.length === 1 ? 'sesión' : 'sesiones'}</span></div>
    <div class="table-wrap" data-component="table"><table class="table box-history"><thead><tr><th>Abrió</th>${METHODS.map(([, l]) => `<th class="num">${esc(l === 'Enlace de pago' ? 'Enlace' : l)}</th>`).join('')}<th class="num">Depositado</th><th class="num">Contado</th><th class="num">Diferencia</th><th>Estado</th><th class="actions"><span class="sr-only">Pagos</span></th></tr></thead><tbody>${rows}</tbody></table></div></section>`;
}

/** The payments of one session (its detail row). */
export function sessionPaymentsHtml(ps: SessionPayment[]) {
  if (!ps.length) return '<p class="muted small">Sin pagos en esta sesión.</p>';
  return `<table class="table"><thead><tr><th>Fecha</th><th>Póliza</th><th>Recibo</th><th class="num">Monto</th><th>Método</th><th>Referencia</th></tr></thead><tbody>${ps
    .map(
      (p) =>
        `<tr><td class="nowrap">${esc(date(p.capturedOn))}</td><td><span class="mono">${esc(p.policyNumber ?? '—')}</span>${p.policyholder ? `<div class="muted small">${esc(p.policyholder)}</div>` : ''}</td><td class="mono">${p.installment}</td><td class="num">${fmt(p.amount)}</td><td>${esc(METHOD_LABEL[p.method] ?? p.method)}</td><td class="mono small">${esc(p.reference)}</td></tr>`,
    )
    .join('')}</tbody></table>`;
}

/**
 * A box by its person: the person's own by their name when the session carries it («Caja de Sofía
 * Rac», #260); anyone else's, or without a name, from its id. The id itself never changes.
 */
const boxLabel = (id: string) => {
  const name = nameOf();
  return name && id === myBoxId() ? `Caja de ${name}` : boxName(id);
};

export async function caja() {
  const head = pageHead(
    'Caja',
    'Abre tu caja con el fondo del día, cobra en efectivo desde <strong>Pólizas y recibos</strong>, deposita al banco y ciérrala contando el efectivo.',
  );
  let html = head;
  // One list answers both: the person's own box (absent while never opened, #242) and the
  // agency's boxes that management follows.
  const boxes =
    runsCashBox() || supervises() ? await api<Box[]>('GET', '/v1/cash-boxes') : ([] as Box[]);
  let history = '';
  if (runsCashBox()) {
    const id = myBoxId();
    const box = boxes.find((b) => b.id === id) ?? null;
    const state = box?.state ?? 'CLOSED';
    // A box never opened has no history (and is not asked for: #242).
    const sessions = box
      ? await api<BoxSession[]>('GET', `/v1/cash-boxes/${encodeURIComponent(id)}/sessions`)
      : [];
    const open = sessions.find((x) => x.state === 'OPEN');
    if (box) history = historyHtml(id, sessions, 'Historial de mi caja');
    let body = '';
    if (state === 'CLOSED')
      body = `<div class="toolbar"><div class="grow"><strong>Abrir la caja</strong><span class="muted small">Cuenta el fondo con el que empiezas el día.</span></div>
        ${field('Fondo inicial', moneyControl({ id: 'box-float', value: '500.00' }))}
        <button type="button" class="btn primary" data-box-open="${esc(id)}">Abrir caja</button></div>`;
    else if (state === 'OPEN')
      body = `<div class="kpis cash-kpis" data-component="cash-kpis"><div class="kpi"><span class="kpi-label">${icon('cash')}Efectivo que debe haber</span><span class="kpi-value">${fmt(box?.expected)}</span><span class="kpi-foot">fondo + cobros en efectivo − depósitos</span></div></div>
        <div class="toolbar"><div class="grow"><strong>Depositar al banco</strong><span class="muted small">Registra cada boleta el mismo día.</span></div>
          ${field('Monto', moneyControl({ id: 'box-deposit' }))}
          ${field('Boleta', textControl({ id: 'box-deposit-ref', placeholder: 'BI-0000000' }))}
          <button type="button" class="btn" data-box-deposit="${esc(id)}">Registrar depósito</button></div>
        ${open ? `<div class="grow"><strong>Arqueo por método</strong><span class="muted small">Lo que cobraste en esta sesión: el efectivo está en la caja; lo demás, en el banco.</span></div>${arqueoHtml(open)}` : ''}
        <div class="toolbar"><div class="grow"><strong>Cerrar la caja</strong><span class="muted small">Cuenta el efectivo; si no cuadra, dirección aprueba la diferencia.</span></div>
          ${field('Efectivo contado', moneyControl({ id: 'box-counted' }))}
          <button type="button" class="btn primary" data-box-close="${esc(id)}">Cerrar caja</button></div>`;
    else
      body = `<p class="note">${icon('alert')}<span>Cerraste con una diferencia; dirección debe aprobarla antes de volver a abrir.</span></p>`;
    html += `<section class="card"><div class="card-head"><span class="monogram lg" aria-hidden="true">${icon('cash')}</span>
      <div class="carrier-name"><h2>Mi caja</h2><small>${esc(boxLabel(id))}</small></div><span class="spacer"></span>${pill(state)}</div>${body}</section>`;
  }
  if (supervises()) {
    html += boxes.length
      ? `<section class="card"><div class="card-head"><h2>Cajas de la agencia</h2></div><div class="table-wrap" data-component="table"><table class="table">
        <thead><tr><th>Caja</th><th>Estado</th><th class="num">Debe haber</th><th class="num">Diferencia</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>
        ${boxes
          .map(
            (
              b,
            ) => `<tr><td>${esc(boxLabel(b.id))}</td><td>${pill(b.state)}</td><td class="num">${b.expected ? fmt(b.expected) : '—'}</td>
            <td class="num">${b.difference ? `<strong style="color:var(${b.difference.amountMinor < 0 ? '--bad' : '--warn'})">${fmt(b.difference)}</strong>` : '—'}</td>
            <td class="actions">${b.state === 'PENDING_APPROVAL' && has('agency_director') ? `<button type="button" class="btn sm primary" data-box-approve="${esc(b.id)}">Aprobar diferencia</button> ` : ''}<button type="button" class="btn sm" data-box-history-open="${esc(b.id)}">Historial</button></td></tr>`,
          )
          .join('')}</tbody></table></div></section>`
      : empty(
          'cash',
          'Ninguna caja abierta todavía',
          'Cuando la cajera abra su caja, aquí verás cuánto efectivo debe tener y las diferencias por aprobar.',
          '',
          2,
        );
    html += '<div data-box-history-slot></div>';
  }
  return html + history;
}

export const actions: Actions = {
  click: {
    boxHistoryOpen: (t, d) => {
      const id = d.boxHistoryOpen;
      const slot = document.querySelector<HTMLElement>('[data-box-history-slot]');
      if (!id || !slot) return;
      void act(t, 'Leyendo el historial de la caja…', async () => {
        const sessions = await api<BoxSession[]>(
          'GET',
          `/v1/cash-boxes/${encodeURIComponent(id)}/sessions`,
        );
        slot.innerHTML = historyHtml(
          id,
          sessions,
          `Historial de la ${boxLabel(id).replace(/^Caja/, 'caja')}`,
        );
        slot.querySelector<HTMLElement>('h2')?.setAttribute('tabindex', '-1');
        slot.querySelector<HTMLElement>('h2')?.focus();
        return `Historial de ${boxLabel(id)}: ${sessions.length} ${sessions.length === 1 ? 'sesión' : 'sesiones'}.`;
      });
    },
    sessionPayments: (t, d) => {
      const sid = d.sessionPayments;
      const row = sid
        ? document.querySelector<HTMLElement>(`[data-session-detail="${CSS.escape(sid)}"]`)
        : null;
      if (!sid || !row) return;
      if (!row.hidden) {
        row.hidden = true;
        t.setAttribute('aria-expanded', 'false');
        return;
      }
      void act(t, 'Leyendo los pagos de la sesión…', async () => {
        const ps = await api<SessionPayment[]>(
          'GET',
          `/v1/cash-boxes/${encodeURIComponent(d.box ?? '')}/sessions/${encodeURIComponent(sid)}/payments`,
        );
        const cell = row.querySelector('td');
        if (cell) cell.innerHTML = sessionPaymentsHtml(ps);
        row.hidden = false;
        t.setAttribute('aria-expanded', 'true');
        return `${ps.length} ${ps.length === 1 ? 'pago' : 'pagos'} en la sesión.`;
      });
    },
    boxOpen: (t, d) => {
      void act(t, 'Abriendo la caja…', async () => {
        const float = qInput('#box-float');
        await api('POST', `/v1/cash-boxes/${encodeURIComponent(d.boxOpen ?? '')}/open`, { float });
        await render(current, { quiet: true });
        return `Caja abierta con un fondo de ${fmt(float)}.`;
      });
    },
    boxDeposit: (t, d) => {
      void act(t, 'Registrando el depósito…', async () => {
        const amount = qInput('#box-deposit');
        const bankReference = textInput('#box-deposit-ref', 'el número de boleta');
        await api('POST', `/v1/cash-boxes/${encodeURIComponent(d.boxDeposit ?? '')}/deposits`, {
          amount,
          bankReference,
        });
        await render(current, { quiet: true });
        return `Depósito de ${fmt(amount)} registrado (boleta ${bankReference}).`;
      });
    },
    boxClose: (t, d) => {
      void act(t, 'Cerrando la caja…', async () => {
        const counted = qInput('#box-counted');
        const r = await api<{ state: string; difference: Money }>(
          'POST',
          `/v1/cash-boxes/${encodeURIComponent(d.boxClose ?? '')}/close`,
          { counted },
        );
        await render(current, { quiet: true });
        return r.difference.amountMinor === 0
          ? 'Caja cerrada y cuadrada.'
          : `Caja cerrada con ${r.difference.amountMinor < 0 ? 'faltante' : 'sobrante'} de ${fmt({ ...r.difference, amountMinor: Math.abs(r.difference.amountMinor) })}. Falta la aprobación de dirección.`;
      });
    },
    boxApprove: (t, d) => {
      void act(t, 'Aprobando la diferencia…', async () => {
        await api(
          'POST',
          `/v1/cash-boxes/${encodeURIComponent(d.boxApprove ?? '')}/approve-difference`,
        );
        await render(current, { quiet: true });
        return `Diferencia de ${d.boxApprove} aprobada y registrada en contabilidad.`;
      });
    },
  },
};
